import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { ServiceQuestion } from './get-question';
import { serviceQuestionKeys } from './query-key-factory';

import { MutationConfig } from '@/lib/react-query';
import { supabase } from '@/lib/supabase-client';
import { logger } from '@/logger';

export const updateServiceQuestionInputSchema = z.object({
  text: z
    .string()
    .min(5, 'Le titre doit contenir au moins 5 caractères')
    .max(70, 'Le titre doit contenir au plus 70 caractères'),
  type: z.custom<ServiceQuestion['type']>(),
  choices: z.custom<ServiceQuestion['choices']>().optional(),
});

export type UpdateServiceQuestionInput = z.infer<
  typeof updateServiceQuestionInputSchema
>;

export const updateServiceQuestion = async ({
  qid,
  values,
}: {
  qid: string;
  values: UpdateServiceQuestionInput;
}) => {
  const { error: questionError } = await supabase
    .from('questions')
    .update({ text: values.text, type: values.type })
    .eq('id', qid);

  if (questionError) {
    logger.error('Failed to update service question', {
      errorMessage: questionError,
      values,
    });
    throw questionError;
  }

  const { error: deleteChoicesError } = await supabase.from('question_choices').delete().eq('question_id', qid);

  if (deleteChoicesError) {
    logger.error('Failed to clear question choices', { errorMessage: deleteChoicesError, qid });
    throw deleteChoicesError;
  }

  const choiceTexts = values.choices?.map((c) => c.choice_text).filter(Boolean) ?? [];

  if (choiceTexts.length > 0) {
    const { error: insertChoicesError } = await supabase.from('question_choices').insert(
      choiceTexts.map((choice_text) => ({ question_id: qid, choice_text })),
    );

    if (insertChoicesError) {
      logger.error('Failed to insert question choices', { errorMessage: insertChoicesError, qid, choiceTexts });
      throw insertChoicesError;
    }
  }

  return { status: 200 };
};

type UseUpdateServiceQuestionOptions = {
  qid: string;
  mutationConfig?: MutationConfig<typeof updateServiceQuestion>;
};

export const useUpdateServiceQuestion = ({
  qid,
  mutationConfig,
}: UseUpdateServiceQuestionOptions) => {
  const queryClient = useQueryClient();

  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: serviceQuestionKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: serviceQuestionKeys.detail(qid),
      });
      onSuccess?.(...args);
    },
    mutationFn: ({ values }: { values: UpdateServiceQuestionInput }) =>
      updateServiceQuestion({ qid, values }),
    ...restConfig,
  });
};
